import { useAnimateBar } from '@mui/x-charts/hooks/animation';
import type { BarProps } from '@mui/x-charts/BarChart';
import type { Ref } from 'react';

type StackedBarChartRoundedTopBarProps = BarProps & {
  topSeriesKeyByRow: Array<string | null>;
  barRadius: number;
  segmentSeparatorColor: string;
  segmentSeparatorWidth: number;
};

const TOP_SEGMENT_OVERSCAN = 0.75;

export const StackedBarChartRoundedTopBar = ({
  topSeriesKeyByRow,
  barRadius,
  segmentSeparatorColor,
  segmentSeparatorWidth,
  ...props
}: StackedBarChartRoundedTopBarProps): JSX.Element => {
  const {
    ownerState,
    dataIndex,
    id,
    color,
    x,
    y,
    width,
    height: barHeight,
    layout,
    xOrigin,
    yOrigin,
    skipAnimation: skipAnimationProp,
    ...rest
  } = props;
  const animatedProps = useAnimateBar({
    x,
    y,
    width,
    height: barHeight,
    xOrigin,
    yOrigin,
    layout,
    skipAnimation: skipAnimationProp,
  });
  const animatedPathRef = animatedProps.ref as Ref<SVGPathElement>;
  const animatedX = animatedProps.x;
  const animatedY = animatedProps.y;
  const animatedWidth = animatedProps.width;
  const animatedHeight = animatedProps.height;
  const topKey = topSeriesKeyByRow[dataIndex];
  const isTop = typeof topKey === 'string' && topKey === String(id);
  const shouldRound = isTop && animatedHeight > 0;
  const filter = ownerState.isHighlighted ? 'brightness(120%)' : undefined;
  const opacity = ownerState.isFaded ? 0.3 : 1;
  const topY = isTop ? animatedY - TOP_SEGMENT_OVERSCAN : animatedY;
  const topHeight = isTop ? animatedHeight + TOP_SEGMENT_OVERSCAN : animatedHeight;
  const segmentRadius = Math.min(barRadius, animatedWidth / 2, animatedHeight / 2);
  const isSolo = isTop;
  const radius = Math.min(barRadius, animatedWidth / 2, animatedHeight);
  if (radius <= 0 || segmentRadius <= 0) {
    return (
      <rect
        {...rest}
        x={animatedX}
        y={topY}
        width={animatedWidth}
        height={topHeight}
        fill={color}
        stroke={segmentSeparatorColor}
        strokeWidth={segmentSeparatorWidth}
        strokeLinejoin="round"
        filter={filter}
        opacity={opacity}
        ref={animatedProps.ref}
        data-highlighted={ownerState.isHighlighted ? true : undefined}
        data-faded={ownerState.isFaded ? true : undefined}
      />
    );
  }
  const right = animatedX + animatedWidth;
  const bottom = topY + topHeight;
  if (isSolo) {
    const roundedTopOnlyPath = [
      `M ${animatedX} ${bottom}`,
      `L ${animatedX} ${topY + radius}`,
      `Q ${animatedX} ${topY} ${animatedX + radius} ${topY}`,
      `L ${right - radius} ${topY}`,
      `Q ${right} ${topY} ${right} ${topY + radius}`,
      `L ${right} ${bottom}`,
      'Z',
    ].join(' ');
    return (
      <path
        {...rest}
        d={roundedTopOnlyPath}
        fill={color}
        stroke="none"
        filter={filter}
        opacity={opacity}
        ref={animatedPathRef}
        data-highlighted={ownerState.isHighlighted ? true : undefined}
        data-faded={ownerState.isFaded ? true : undefined}
      />
    );
  }
  if (!shouldRound) {
    return (
      <rect
        {...rest}
        x={animatedX}
        y={topY}
        width={animatedWidth}
        height={topHeight}
        fill={color}
        stroke={segmentSeparatorColor}
        strokeWidth={segmentSeparatorWidth}
        strokeLinejoin="round"
        filter={filter}
        opacity={opacity}
        ref={animatedProps.ref}
        data-highlighted={ownerState.isHighlighted ? true : undefined}
        data-faded={ownerState.isFaded ? true : undefined}
      />
    );
  }
  const roundedPath = [
    `M ${animatedX} ${bottom}`,
    `L ${animatedX} ${topY + radius}`,
    `Q ${animatedX} ${topY} ${animatedX + radius} ${topY}`,
    `L ${right - radius} ${topY}`,
    `Q ${right} ${topY} ${right} ${topY + radius}`,
    `L ${right} ${bottom}`,
    'Z',
  ].join(' ');
  return (
    <path
      {...rest}
      d={roundedPath}
      fill={color}
      stroke="none"
      filter={filter}
      opacity={opacity}
      ref={animatedPathRef}
      data-highlighted={ownerState.isHighlighted ? true : undefined}
      data-faded={ownerState.isFaded ? true : undefined}
    />
  );
};
